import { supabase } from '../supabaseClient';

export const PortalApplicationsService = {
  async listVisaApplications(clientId) {
    if (!clientId) return { data: [], error: new Error('Missing client id') };
    try {
      const { data, error } = await supabase
        .from('visa_applications')
        .select(`
          id,
          case_id,
          status,
          submitted_at,
          decision_at,
          updated_at,
          visa_products (
            country,
            visa_type
          )
        `)
        .eq('client_id', clientId)
        .order('updated_at', { ascending: false });
      if (error) throw error;

      // Flatten structure
      const formattedData = (data || []).map(app => ({
        ...app,
        country: app.visa_products?.country,
        visa_type: app.visa_products?.visa_type
      }));

      return { data: formattedData, error: null };
    } catch (error) {
      console.error('[PortalApplicationsService] listVisaApplications failed', error);
      return { data: [], error };
    }
  },

  async listUniversityApplications(clientId) {
    if (!clientId) return { data: [], error: new Error('Missing client id') };
    try {
      const { data, error } = await supabase
        .from('uni_applications')
        .select(`
          id,
          case_id,
          status,
          submitted_at,
          decision_at,
          updated_at,
          programs (
            name,
            university: universities (
              name
            )
          ),
          intake: intakes (
            term
          )
        `)
        .eq('client_id', clientId)
        .order('updated_at', { ascending: false });
      if (error) throw error;

      // Flatten the structure for easier consumption if needed, or keep as is.
      // For now, returning as is, but client might expect 'university_name' etc.
      // Let's map it to match the previous expected structure roughly
      const mapped = data.map(app => ({
        ...app,
        university_name: app.programs?.university?.name || 'Unknown University',
        program_name: app.programs?.name || 'Unknown Program',
        intake: app.intake?.term
      }));

      return { data: mapped, error: null };
    } catch (error) {
      console.error('[PortalApplicationsService] listUniversityApplications failed', error);
      return { data: [], error };
    }
  },

  async listCases(clientId) {
    if (!clientId) return { data: [], error: new Error('Missing client id') };
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('id, title, status, created_at, assigned_to, priority')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('[PortalApplicationsService] listCases failed', error);
      return { data: [], error };
    }
  },

  async getCaseDetails(id) {
    if (!id) return { data: null, error: new Error('Missing case id') };
    try {
      // Fetch case details
      const { data: caseData, error: caseError } = await supabase
        .from('cases')
        .select('*')
        .eq('id', id)
        .single();

      if (caseError) throw caseError;

      // Use Promise.allSettled for resilience - if some queries fail (RLS, etc), page still loads
      const results = await Promise.allSettled([
        // 0: Tasks
        supabase
          .from('case_tasks')
          .select('id, title, status, due_date, stage_id, description, assigned_to')
          .eq('case_id', id)
          .order('due_date', { ascending: true }),

        // 1: University Applications
        supabase
          .from('uni_applications')
          .select(`
            id, status, submitted_at, updated_at,
            programs (name, university: universities (name)),
            intakes (term, apply_close)
          `)
          .eq('case_id', id),

        // 2: Visa Applications
        supabase
          .from('visa_applications')
          .select(`
            id, status, submitted_at, updated_at,
            visa_products (country, visa_type)
          `)
          .eq('case_id', id),

        // 3: Case Stages
        supabase
          .from('case_stages')
          .select('id, title, stage_key, status, sort_order, started_at, completed_at')
          .eq('case_id', id)
          .order('sort_order', { ascending: true }),

        // 4: Application Offers
        supabase
          .from('application_offers')
          .select(`
            id, offer_type, accepted, accepted_at,
            issue_date, expires_at, tuition_total, scholarship_amount,
            currency, deposit_amount, enrollment_deadline, conditions_json,
            uni_application_id
          `)
          .eq('case_id', id)
          .order('issue_date', { ascending: false })
      ]);

      // Helper to safely extract data from settled promises
      const getData = (idx) => {
        const result = results[idx];
        if (result.status === 'fulfilled' && !result.value.error) {
          return result.value.data || [];
        }
        // Log but don't throw for failed queries
        if (result.status === 'rejected') {
          console.warn(`[PortalApplicationsService] Query ${idx} rejected:`, result.reason);
        } else if (result.value?.error) {
          console.warn(`[PortalApplicationsService] Query ${idx} error:`, result.value.error);
        }
        return [];
      };

      const tasksData = getData(0);
      const uniAppsData = getData(1);
      const visaAppsData = getData(2);
      const stagesData = getData(3);
      const offersData = getData(4);

      // Process Uni Apps
      const uniApps = uniAppsData.map(app => ({
        ...app,
        university_name: app.programs?.university?.name,
        program_name: app.programs?.name,
        intake_term: app.intakes?.term,
        deadline: app.intakes?.apply_close,
        doc_progress: { done: 0, total: 0 } // Simplified - doc counting deferred
      }));

      // Process Visa Apps
      const visaApps = visaAppsData.map(app => ({
        ...app,
        country: app.visa_products?.country,
        visa_type: app.visa_products?.visa_type,
        doc_progress: { done: 0, total: 0 }
      }));

      // Process offers to link with application info
      const offers = offersData.map(offer => {
        const linkedApp = uniApps.find(a => a.id === offer.uni_application_id);
        return {
          ...offer,
          university_name: linkedApp?.university_name,
          program_name: linkedApp?.program_name
        };
      });

      const result = {
        ...caseData,
        tasks: tasksData,
        uni_applications: uniApps,
        visa_applications: visaApps,
        stages: stagesData,
        offers: offers
      };

      return { data: result, error: null };
    } catch (error) {
      console.error('[PortalApplicationsService] getCaseDetails failed', error);
      return { data: null, error };
    }
  },


  async listMilestones(applicationId, type = 'visa') {
    if (!applicationId) return { data: [], error: new Error('Missing application id') };
    const table = type === 'visa' ? 'visa_milestones' : 'uni_app_documents';
    const foreignKey = type === 'visa' ? 'visa_application_id' : 'uni_application_id';

    try {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq(foreignKey, applicationId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('[PortalApplicationsService] listMilestones failed', error);
      return { data: [], error };
    }
  },

  async getUniversityApplication(id) {
    if (!id) return { data: null, error: new Error('Missing id') };
    try {
      const { data, error } = await supabase
        .from('uni_applications')
        .select(`
          *,
          programs (
            name,
            university: universities (
              name,
              logo_url
            )
          ),
          intake: intakes (
            term
          ),
          offers: application_offers (
            *
          ),
          documents: uni_app_documents (
            *
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[PortalApplicationsService] getUniversityApplication failed', error);
      return { data: null, error };
    }
  },

  async getVisaApplication(id) {
    if (!id) return { data: null, error: new Error('Missing id') };
    try {
      const { data, error } = await supabase
        .from('visa_applications')
        .select(`
          *,
          visa_products (
            country,
            visa_type,
            name
          ),
          milestones: visa_milestones (
            *
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[PortalApplicationsService] getVisaApplication failed', error);
      return { data: null, error };
    }
  },

  async acceptOffer(offerId) {
    if (!offerId) return { data: null, error: new Error('Missing offer id') };
    try {
      // First, get the offer to know the application id
      const { data: offer, error: fetchError } = await supabase
        .from('application_offers')
        .select('uni_application_id')
        .eq('id', offerId)
        .single();

      if (fetchError) throw fetchError;

      // Update the offer status
      const { data, error } = await supabase
        .from('application_offers')
        .update({ status: 'accepted', updated_at: new Date().toISOString() })
        .eq('id', offerId)
        .select()
        .single();

      if (error) throw error;

      // Optionally update the application status to 'offer_accepted'
      if (offer?.uni_application_id) {
        await supabase
          .from('uni_applications')
          .update({ status: 'offer_accepted', updated_at: new Date().toISOString() })
          .eq('id', offer.uni_application_id);
      }

      return { data, error: null };
    } catch (error) {
      console.error('[PortalApplicationsService] acceptOffer failed', error);
      return { data: null, error };
    }
  }
};

